import { type VisaListItem } from '@utazzo/shared';
import { UzBadge } from '@utazzo/ui';
import { Check, ChevronsUpDown, FileText, Loader2, MapPin, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { CountryPicker } from './CountryPicker';

import { useCurrentUser } from '@/features/auth/use-auth';
import * as visasApi from '@/features/visas/visas.api';

interface VisaPickerDialogProps {
  label?: string;
  value: string | null;
  onValueChange: (value: string | null) => void;
  /** Default country filter; the operator can change it in the dialog. */
  preferCountryId?: string | null;
  /**
   * Strict-country mode: locks the country filter to `preferCountryId`. Mirrors
   * the old picker's `restrictToCountry`.
   */
  restrictToCountry?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Optional label to show immediately while the selected visa is fetched. */
  initialLabel?: string | null;
}

/**
 * Dialog-style visa product picker. Trigger renders like a dropdown; click
 * opens a full-detail dialog with search + country filter (like the day-item
 * picker used for activities and transfers). Selecting a card fills the
 * trigger with the same `visaId` value the old combobox produced.
 */
export const VisaPickerDialog = ({
  label = 'Visa product',
  value,
  onValueChange,
  preferCountryId,
  restrictToCountry = false,
  disabled,
  placeholder = 'Select a visa',
  initialLabel = null,
}: VisaPickerDialogProps): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<VisaListItem | null>(null);

  // Trigger label needs a fetch when the parent controls value and we don't
  // have the row cached yet.
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    if (selected && selected.id === value) return;
    let cancelled = false;
    void (async () => {
      try {
        const v = await visasApi.getVisa(value);
        if (!cancelled) {
          setSelected({
            id: v.id,
            legacyId: v.legacyId,
            legacyCode: v.legacyCode,
            slug: v.slug,
            status: v.status,
            name: v.name,
            visaType: v.visaType,
            entryType: v.entryType,
            processingMode: v.processingMode,
            targetCountryId: v.targetCountryId,
            targetCountryName: v.targetCountryName,
            validityMonths: v.validityMonths,
            maxStayDays: v.maxStayDays,
            vendorSupplierId: v.vendorSupplierId,
            vendorSupplierName: v.vendorSupplierName,
            pricingTierCount: v.pricingTierCount,
            // Trigger label doesn't need these; nulling out is fine and keeps
            // the state shape aligned with VisaListItem.
            defaultTierName: null,
            defaultTierFeePerApplicantInrPaise: null,
            isApprovedByAdmin: v.isApprovedByAdmin,
            photoCount: v.photoCount,
            createdAt: v.createdAt,
          });
        }
      } catch {
        /* ignore - trigger falls back to initialLabel / placeholder */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value, selected]);

  const triggerText = selected
    ? formatTriggerLabel(selected)
    : value && initialLabel
      ? initialLabel
      : null;

  return (
    <div className="space-y-1.5">
      {label && <label className="text-foreground text-sm leading-none font-medium">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="border-input bg-background focus:border-ring flex h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className={triggerText ? 'truncate' : 'text-muted-foreground truncate'}>
          {triggerText ?? placeholder}
        </span>
        <span className="ml-2 flex shrink-0 items-center gap-1">
          {value && !disabled && (
            <span
              aria-label="Clear selection"
              role="button"
              tabIndex={-1}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onValueChange(null);
                setSelected(null);
              }}
              className="text-muted-foreground hover:text-foreground rounded-sm p-0.5"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </span>
      </button>

      {open && (
        <VisaPickerBody
          initialCountryId={preferCountryId ?? null}
          restrictToCountry={restrictToCountry}
          selectedId={value ?? null}
          onClose={() => setOpen(false)}
          onSelect={(row) => {
            setSelected(row);
            onValueChange(row.id);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
};

const formatTriggerLabel = (v: VisaListItem): string => {
  const modeLabel =
    v.processingMode === 'APPOINTMENT_ONLY'
      ? ' (Appointment only)'
      : v.processingMode === 'APPOINTMENT_EMBASSY'
        ? ' (Appointment + Embassy)'
        : '';
  return `${v.name} - ${humaniseEnum(v.visaType)}${modeLabel}`;
};

// ---------------------------------------------------------------------------
// Dialog body
// ---------------------------------------------------------------------------

interface VisaPickerBodyProps {
  initialCountryId: string | null;
  restrictToCountry: boolean;
  selectedId: string | null;
  onClose: () => void;
  onSelect: (row: VisaListItem) => void;
}

const VisaPickerBody = ({
  initialCountryId,
  restrictToCountry,
  selectedId,
  onClose,
  onSelect,
}: VisaPickerBodyProps): React.JSX.Element => {
  // Prices in this picker are ADMIN-only. Sales-side roles pick a visa by
  // name / type / country + the default plan gets applied silently.
  const showPrices = useCurrentUser()?.role === 'ADMIN';
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [countryId, setCountryId] = useState<string | null>(initialCountryId);
  const [rows, setRows] = useState<VisaListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    // Autofocus search on open; escape closes.
    searchInputRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await visasApi.listVisas({
          page: 1,
          pageSize: debouncedSearch ? 30 : 60,
          sortDir: 'asc',
          ...(countryId ? { targetCountryId: countryId } : {}),
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        });
        if (cancelled) return;
        // When a country is chosen but not enforced, still surface that
        // country's visas at the top when the user cleared search.
        let data = res.data;
        if (!debouncedSearch && countryId) {
          data = [...data].sort((a, b) => {
            const aPref = a.targetCountryId === countryId ? 0 : 1;
            const bPref = b.targetCountryId === countryId ? 0 : 1;
            return aPref - bPref;
          });
        }
        setRows(data);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, countryId]);

  const countryOptions = useMemo(() => {
    const set = new Map<string, string>();
    for (const r of rows) {
      if (r.targetCountryId && r.targetCountryName) set.set(r.targetCountryId, r.targetCountryName);
    }
    return set;
  }, [rows]);
  void countryOptions;

  return createPortal(
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-zinc-900/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex h-[82vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between border-b border-zinc-100 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <FileText className="text-primary h-4 w-4" aria-hidden />
            <div>
              <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
                Pick a visa product
              </h3>
              <p className="mt-0.5 text-xs text-zinc-500">
                {restrictToCountry
                  ? 'Restricted to the trip destination country.'
                  : 'Search across every country, or filter by destination.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="grid gap-2 border-b border-zinc-100 px-5 py-3 md:grid-cols-[1fr_260px]">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              ref={searchInputRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search visas..."
              className="border-input focus:border-ring h-9 w-full rounded-md border pr-3 pl-8 text-sm focus:outline-none"
            />
          </div>
          <div>
            <CountryPicker
              label=""
              value={countryId}
              onValueChange={(v) => setCountryId(v)}
              disabled={restrictToCountry}
              hint={undefined}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-zinc-50/40 p-3">
          {loading ? (
            <div className="flex h-full items-center justify-center text-zinc-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading visas
            </div>
          ) : rows.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-zinc-500">
              No visas match this filter.
            </div>
          ) : (
            <ul className="space-y-2">
              {rows.map((row) => (
                <li key={row.id}>
                  <VisaCard
                    row={row}
                    selected={row.id === selectedId}
                    showPrices={showPrices}
                    onSelect={onSelect}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

interface VisaCardProps {
  row: VisaListItem;
  selected: boolean;
  showPrices: boolean;
  onSelect: (row: VisaListItem) => void;
}

const VisaCard = ({ row, selected, showPrices, onSelect }: VisaCardProps): React.JSX.Element => {
  const hasMultipleTiers = row.pricingTierCount > 1;
  return (
    <button
      type="button"
      onClick={() => onSelect(row)}
      className={`group hover:border-primary/60 w-full rounded-lg border bg-white p-3 text-left shadow-sm transition-colors hover:shadow ${
        selected ? 'border-primary ring-primary/40 ring-1' : 'border-zinc-200'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-start gap-2">
            <h4 className="min-w-0 flex-1 text-sm font-semibold break-words text-zinc-900">
              {row.name}
            </h4>
            {selected && <Check className="text-primary h-4 w-4 shrink-0" aria-label="Selected" />}
          </div>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-zinc-500">
            <MapPin className="h-3 w-3" aria-hidden />
            {row.targetCountryName || 'No country'}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
          <UzBadge tone="secondary">{humaniseEnum(row.visaType)}</UzBadge>
          <UzBadge tone="secondary">{humaniseEnum(row.entryType)}</UzBadge>
          {row.processingMode !== 'STANDARD' && (
            <UzBadge tone="warning">{humaniseEnum(row.processingMode)}</UzBadge>
          )}
        </div>
      </div>

      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-zinc-600 sm:grid-cols-3">
        {row.defaultTierName && (
          <MetaRow icon={<FileText className="h-3 w-3" />} label="Plan">
            {row.defaultTierName}
            {hasMultipleTiers ? ' (default)' : ''}
          </MetaRow>
        )}
        {showPrices && row.defaultTierFeePerApplicantInrPaise && (
          <MetaRow icon={<FileText className="h-3 w-3" />} label="From">
            {formatInrPaise(row.defaultTierFeePerApplicantInrPaise)} /pax
          </MetaRow>
        )}
      </dl>
      {hasMultipleTiers && (
        <p className="mt-1.5 text-[11px] text-zinc-400">
          Multiple plans available. Default plan is applied on selection.
        </p>
      )}
    </button>
  );
};

const MetaRow = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div className="flex items-center gap-1.5">
    <span className="text-zinc-400">{icon}</span>
    <dt className="text-zinc-500">{label}:</dt>
    <dd className="font-medium text-zinc-800">{children}</dd>
  </div>
);

/** Format an INR-paise string as a whole-rupee amount with the ₹ symbol. */
const formatInrPaise = (paise: string | null | undefined): string => {
  if (!paise) return '-';
  const n = Number(paise) / 100;
  if (!Number.isFinite(n)) return '-';
  return '₹' + Math.round(n).toLocaleString('en-IN');
};

const humaniseEnum = (s: string): string =>
  s
    .split('_')
    .map((w) => w[0]?.toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
